/**
 * MACROSCOPIC SCALE - species transport on an axisymmetric chamber cross-section.
 *
 * Two species are carried: the feed gas (CF4) that arrives through the
 * showerhead, and the etchant radical (F) that the plasma makes from it and the
 * wafer consumes. They are solved to steady state rather than marched in time,
 * and that choice is the model's central multiscale move:
 *
 *   transport equilibrates in milliseconds (D ~ 1 m^2/s at 20 mTorr)
 *   the film clears in minutes
 *
 * Four orders of magnitude apart, so the gas field is always in quasi-steady
 * state with respect to the etch. Solving the steady equation directly buys a
 * factor of ~10^4 in work over an explicit time march and loses nothing a
 * technician would observe. The film is then advanced on its own slow clock.
 *
 * Discretisation is finite volume with upwind advection, solved by successive
 * over-relaxation. Axisymmetric metrics (face area proportional to r, cell
 * volume proportional to r) are carried explicitly, so the r = 0 face has zero
 * area and the symmetry condition on the axis is enforced by construction
 * rather than by a special case.
 */

import { CHAMBER, type FlowField, type Geometry } from './flow'
import { clamp } from '../core/math'

export interface SolverGrid {
  nr: number
  nz: number
  dr: number
  dz: number
  /** Cell-centre radii [m], length nr. */
  rc: Float64Array
  /** Cell-centre heights [m], length nz. */
  zc: Float64Array
}

export function makeGrid(geom: Geometry, nr: number, nz: number): SolverGrid {
  const dr = geom.radius / nr
  const dz = geom.height / nz
  const rc = new Float64Array(nr)
  const zc = new Float64Array(nz)
  for (let i = 0; i < nr; i++) rc[i] = (i + 0.5) * dr
  for (let j = 0; j < nz; j++) zc[j] = (j + 0.5) * dz
  return { nr, nz, dr, dz, rc, zc }
}

/** Everything the transport step needs that comes from elsewhere in the model. */
export interface TransportParams {
  flow: FlowField
  /** Species diffusivity [m^2/s]. */
  diffusivity: number
  /** Feed density supplied at the showerhead [m^-3]. */
  inletFeedDensity: number
  /** First-order dissociation rate constant in the plasma [1/s]. */
  dissociationRate: number
  /** Etchant atoms produced per feed molecule dissociated. */
  etchantYield: number
  /** Wall loss probability for the radical (0-1), rises with wall seasoning. */
  wallRecombination: number
  /**
   * First-order loss of the radical in the gas phase [1/s].
   *
   * Zero for a tight chamber. A leak admits air, and oxygen scavenges fluorine
   * before it ever reaches the wafer - which is why a leak starves the etch
   * while flow and power readings both look perfectly normal.
   */
  etchantVolumeLoss: number
  /** Mean thermal speed of the radical [m/s]. */
  thermalSpeed: number
  /**
   * Surface consumption closure. Given the local radical density at the wafer,
   * returns the consumed flux [m^-2 s^-1]. Nonlinear, so the solver Picard
   * iterates on it.
   */
  surfaceSink(radialIndex: number, density: number): number
  /**
   * Surface consumption of the FEED species at the wafer.
   *
   * Etch and deposition consume different things. An etcher consumes a radical
   * the plasma made from the feed, so the sink belongs on the product species.
   * A deposition tool consumes the delivered precursor itself, with no
   * intermediate - so its sink belongs on the feed, and its product species is
   * unused entirely. Supporting both is what lets one transport solver serve
   * both modules.
   */
  feedSurfaceSink?: (radialIndex: number, density: number) => number
  /** Wall loss probability for the feed species, if it adsorbs on walls. */
  feedWallRecombination?: number
}

const OMEGA = 1.72 // over-relaxation factor
const AXIAL_SOURCE_CENTRE = 0.62 // plasma centroid as a fraction of chamber height
const AXIAL_SOURCE_WIDTH = 0.3

export class ReactorSolver {
  readonly grid: SolverGrid
  readonly geom: Geometry

  /** Feed gas density field [m^-3]. */
  readonly feed: Float64Array
  /** Etchant radical density field [m^-3]. */
  readonly etchant: Float64Array

  /** Normalised plasma power-deposition shape, volume-average 1. */
  private readonly plasmaShape: Float64Array

  /** Cell volume factor r*dr*dz (2*pi dropped consistently throughout). */
  private readonly vol: Float64Array

  // Geometric + flow coefficients, rebuilt when the recipe changes.
  private readonly aE: Float64Array
  private readonly aW: Float64Array
  private readonly aN: Float64Array
  private readonly aS: Float64Array
  private readonly feedAp: Float64Array
  private readonly feedB: Float64Array
  private readonly etchApBase: Float64Array
  private readonly waferFaceArea: Float64Array

  private params: TransportParams | null = null
  /** Index of the last wafer cell in r; beyond this is focus ring / chuck edge. */
  private readonly waferCells: number

  /**
   * Grid resolution is chosen for CONVERGENCE, not for detail.
   *
   * SOR needs iterations proportional to roughly the square of the cell count,
   * and measured on this problem a 64x32 grid does not close its mass balance
   * at all within twenty thousand sweeps, while 32x16 reaches an imbalance of
   * 1e-4 in about 120 ms. The finer grid was strictly worse: slower AND wrong.
   * A converged coarse answer beats an unconverged fine one, and 25 radial
   * cells across the wafer is still finer than the 49-site pattern a metrology
   * tool actually measures.
   */
  constructor(geom: Geometry = CHAMBER, nr = 32, nz = 16) {
    this.geom = geom
    this.grid = makeGrid(geom, nr, nz)
    const n = nr * nz

    this.feed = new Float64Array(n)
    this.etchant = new Float64Array(n)
    this.plasmaShape = new Float64Array(n)
    this.vol = new Float64Array(n)
    this.aE = new Float64Array(n)
    this.aW = new Float64Array(n)
    this.aN = new Float64Array(n)
    this.aS = new Float64Array(n)
    this.feedAp = new Float64Array(n)
    this.feedB = new Float64Array(n)
    this.etchApBase = new Float64Array(n)
    this.waferFaceArea = new Float64Array(nr)

    this.waferCells = Math.max(1, Math.round(geom.waferRadius / this.grid.dr))

    this.buildStaticFields()
  }

  private idx(i: number, j: number): number {
    return j * this.grid.nr + i
  }

  private buildStaticFields(): void {
    const { nr, nz, dr, dz, rc, zc } = this.grid
    const H = this.geom.height
    const R = this.geom.radius

    // Cell volumes and the raw plasma shape.
    let weighted = 0
    let totalVol = 0
    for (let j = 0; j < nz; j++) {
      for (let i = 0; i < nr; i++) {
        const k = this.idx(i, j)
        const v = rc[i] * dr * dz
        this.vol[k] = v

        const zt = (zc[j] - AXIAL_SOURCE_CENTRE * H) / (AXIAL_SOURCE_WIDTH * H)
        const radial = 1 - 0.25 * (rc[i] / R) ** 2
        const g = Math.exp(-zt * zt) * Math.max(radial, 0)
        this.plasmaShape[k] = g

        weighted += g * v
        totalVol += v
      }
    }
    // Normalise so the volume-average of the shape is exactly 1.
    const norm = weighted > 0 ? totalVol / weighted : 1
    for (let k = 0; k < this.plasmaShape.length; k++) this.plasmaShape[k] *= norm

    for (let i = 0; i < nr; i++) this.waferFaceArea[i] = rc[i] * dr
  }

  /**
   * Rebuild the linear-system coefficients. Called whenever the recipe, plasma
   * state, or chamber condition changes - not every frame.
   */
  setParams(params: TransportParams): void {
    this.params = params
    const { nr, nz, dr, dz, rc } = this.grid
    const { flow, diffusivity: D, wallRecombination, thermalSpeed } = params

    /** Deposition velocity for wall loss of the radical [m/s]. */
    const wallLoss = 0.25 * thermalSpeed * wallRecombination
    const feedWallLoss = 0.25 * thermalSpeed * (params.feedWallRecombination ?? 0)

    for (let j = 0; j < nz; j++) {
      for (let i = 0; i < nr; i++) {
        const k = this.idx(i, j)
        const r = rc[i]


        // --- face areas (2*pi dropped) ---
        const areaE = (i + 1) * dr * dz
        const areaW = i * dr * dz // exactly zero on the axis
        const areaN = r * dr
        const areaS = r * dr

        // --- outward convective fluxes, from stream-function differences ---
        //
        // Taken as differences of psi rather than (velocity x area) so that the
        // four faces of every cell sum to exactly zero. Point-sampling the
        // velocity at face centres leaves a discrete divergence error that
        // behaves as a source of gas: it let the model destroy 2.6x more feed
        // than entered the chamber, and it is why flow was almost inert.
        //
        //   axial face (normal +z), annulus r1..r2:  psi(r2, z) - psi(r1, z)
        //   radial face (normal +r), span z1..z2:    psi(r, z1) - psi(r, z2)
        const rW = i * dr
        const rE = (i + 1) * dr
        const zS = j * dz
        const zN = (j + 1) * dz

        const fE = flow.psi(rE, zS) - flow.psi(rE, zN)
        const fW = -(flow.psi(rW, zS) - flow.psi(rW, zN))
        const fN = flow.psi(rE, zN) - flow.psi(rW, zN)
        const fS = -(flow.psi(rE, zS) - flow.psi(rW, zS))

        // --- diffusive conductances ---
        const dE = (D * areaE) / dr
        const dW = (D * areaW) / dr
        const dN = (D * areaN) / dz
        const dS = (D * areaS) / dz

        let aP = 0
        let bFeed = 0
        let apEtchExtra = 0
        let apFeedExtra = 0

        // East: interior neighbour, or the chamber wall / pump exit.
        if (i < nr - 1) {
          this.aE[k] = dE + Math.max(-fE, 0)
          aP += dE + Math.max(fE, 0)
        } else {
          this.aE[k] = 0
          // Convective outflow only; zero-gradient for diffusion.
          aP += Math.max(fE, 0)
          apEtchExtra += areaE * wallLoss
          apFeedExtra += areaE * feedWallLoss
        }

        // West: interior neighbour, or the symmetry axis (area 0, so inert).
        this.aW[k] = i > 0 ? dW + Math.max(-fW, 0) : 0
        if (i > 0) aP += dW + Math.max(fW, 0)

        // North: interior neighbour, or the showerhead.
        if (j < nz - 1) {
          this.aN[k] = dN + Math.max(-fN, 0)
          aP += dN + Math.max(fN, 0)
        } else {
          this.aN[k] = 0
          // Inlet is a SPECIFIED FLUX, not a specified density. The mass flow
          // controller sets how many molecules per second enter; the throttle
          // valve sets pressure. Pinning the density here instead would let the
          // chamber draw unlimited feed by diffusion, and gas flow would stop
          // mattering - which is exactly the symptom that exposed the bug.
          //
          // Summed over the showerhead, |u_z| * A * n_inlet is precisely the
          // molecular throughput implied by the sccm setpoint.
          aP += Math.max(fN, 0)
          bFeed += Math.max(-fN, 0) * params.inletFeedDensity
          // The radical sees the showerhead as a recombining wall.
          apEtchExtra += areaN * wallLoss
          apFeedExtra += areaN * feedWallLoss
        }

        // South: interior neighbour, or the wafer / chuck plane.
        if (j > 0) {
          this.aS[k] = dS + Math.max(-fS, 0)
          aP += dS + Math.max(fS, 0)
        } else {
          this.aS[k] = 0
          // Wafer consumption is nonlinear and added per Picard pass; the chuck
          // and focus ring outside the wafer just recombine.
          if (i >= this.waferCells) {
            apEtchExtra += areaS * wallLoss
            apFeedExtra += areaS * feedWallLoss
          }
        }

        // Volume term: dissociation removes feed and makes radical.
        const diss = params.dissociationRate * this.plasmaShape[k]
        const v = this.vol[k]

        this.feedAp[k] = aP + v * diss + apFeedExtra
        this.feedB[k] = bFeed
        this.etchApBase[k] = aP + apEtchExtra + v * params.etchantVolumeLoss
      }
    }
  }

  /** Reset both fields to a sensible starting guess. */
  seed(feedDensity: number): void {
    this.feed.fill(feedDensity)
    this.etchant.fill(feedDensity * 0.05)
  }

  /**
   * Advance the steady-state solution.
   *
   * `sweeps` controls effort: a few hundred is enough to track a slow drift
   * frame to frame, a few thousand converges from cold after a recipe change.
   * Returns the final relative residual so callers can report convergence
   * honestly rather than assuming it.
   */
  solve(sweeps: number, tolerance = 1e-3): number {
    const p = this.params
    if (!p) return 0

    let residual = 1
    // Check the integral balance rather than every pass; it costs a full sweep
    // over both fields, and convergence is smooth enough not to need it often.
    const CHECK_EVERY = 25
    for (let pass = 0; pass < sweeps; pass++) {
      if (pass > 0 && pass % CHECK_EVERY === 0) {
        residual = this.imbalance()
        if (residual < tolerance) break
      }
      // BOTH fields must be converged, not just the etchant. Testing only the
      // etchant let the solver exit while the feed equation was still ~17% out
      // of balance, and since the etchant source is proportional to the feed,
      // an unconverged feed field silently manufactured fluorine: the chamber
      // dissociated nearly 3x more CF4 than entered it. That is what made gas
      // flow behave as an almost inert knob.
      this.sweepFeed()
      this.sweepEtchant(p)
    }
    return this.imbalance()
  }

  /**
   * Convergence measured as the worst normalised imbalance of the two
   * conservation equations - what actually goes in versus what comes out.
   *
   * The obvious proxy, "largest cell-to-cell change this sweep", is what was
   * used before and it lies: SOR settles into small per-sweep steps long before
   * the integral balance closes, so that measure read 6e-5 while the chamber
   * was destroying twice the feed gas that entered it. Every number the model
   * produced sat on that unconverged solution. Measuring the balance directly
   * costs one extra sweep per check and cannot be fooled the same way.
   */
  private imbalance(): number {
    const p = this.params
    if (!p) return 0
    const { nr, nz } = this.grid

    let feedIn = 0
    let feedOut = 0
    let etchIn = 0
    let etchOut = 0

    for (let j = 0; j < nz; j++) {
      for (let i = 0; i < nr; i++) {
        const k = this.idx(i, j)
        const v = this.vol[k]
        const diss = p.dissociationRate * this.plasmaShape[k]

        // --- feed: inlet in, dissociation and pump-out out ---
        feedIn += this.feedB[k]
        let feedAp = this.feedAp[k]
        if (j === 0 && i < this.waferCells && p.feedSurfaceSink) {
          const density = Math.max(this.feed[k], 1e-6)
          feedAp += this.waferFaceArea[i] * (p.feedSurfaceSink(i, density) / density)
        }
        const feedNeighbours =
          (i < nr - 1 ? this.aE[k] * this.feed[k + 1] : 0) +
          (i > 0 ? this.aW[k] * this.feed[k - 1] : 0) +
          (j < nz - 1 ? this.aN[k] * this.feed[k + nr] : 0) +
          (j > 0 ? this.aS[k] * this.feed[k - nr] : 0)
        feedOut += Math.abs(feedAp * this.feed[k] - feedNeighbours - this.feedB[k])

        // --- etchant: plasma source in, all losses out ---
        const source = v * diss * this.feed[k] * p.etchantYield
        etchIn += source
        let ap = this.etchApBase[k]
        if (j === 0 && i < this.waferCells) {
          const density = Math.max(this.etchant[k], 1e-6)
          ap += this.waferFaceArea[i] * (p.surfaceSink(i, density) / density)
        }
        const etchNeighbours =
          (i < nr - 1 ? this.aE[k] * this.etchant[k + 1] : 0) +
          (i > 0 ? this.aW[k] * this.etchant[k - 1] : 0) +
          (j < nz - 1 ? this.aN[k] * this.etchant[k + nr] : 0) +
          (j > 0 ? this.aS[k] * this.etchant[k - nr] : 0)
        etchOut += Math.abs(ap * this.etchant[k] - etchNeighbours - source)
      }
    }

    const feedErr = feedIn > 0 ? feedOut / feedIn : 0
    const etchErr = etchIn > 0 ? etchOut / etchIn : 0
    return Math.max(feedErr, etchErr)
  }

  /** One red-black SOR pass over the feed field. Returns its relative residual. */
  private sweepFeed(): number {
    const { nr, nz } = this.grid
    const f = this.feed
    const sink = this.params?.feedSurfaceSink
    let maxDelta = 0
    let maxValue = 1e-30
    // Red-black ordering keeps the sweep order-independent and lets SOR run at
    // a high relaxation factor without oscillating.
    for (let colour = 0; colour < 2; colour++) {
      for (let j = 0; j < nz; j++) {
        for (let i = (j + colour) & 1; i < nr; i += 2) {
          const k = this.idx(i, j)
          let ap = this.feedAp[k]
          // Deposition consumes the feed itself at the wafer. Nonlinear in the
          // local density (sites saturate), so it is linearised here each pass
          // exactly as the etchant sink is.
          if (j === 0 && i < this.waferCells && sink) {
            const density = Math.max(f[k], 1e-6)
            ap += this.waferFaceArea[i] * (sink(i, density) / density)
          }
          if (ap <= 0) continue
          let sum = this.feedB[k]
          if (i < nr - 1) sum += this.aE[k] * f[k + 1]
          if (i > 0) sum += this.aW[k] * f[k - 1]
          if (j < nz - 1) sum += this.aN[k] * f[k + nr]
          if (j > 0) sum += this.aS[k] * f[k - nr]
          const next = sum / ap
          const delta = Math.abs(next - f[k])
          if (delta > maxDelta) maxDelta = delta
          f[k] += OMEGA * (next - f[k])
          if (f[k] < 0) f[k] = 0
          if (f[k] > maxValue) maxValue = f[k]
        }
      }
    }
    return maxDelta / maxValue
  }

  private sweepEtchant(p: TransportParams): number {
    const { nr, nz } = this.grid
    const e = this.etchant
    const yieldPerDiss = p.etchantYield

    let maxDelta = 0
    let maxValue = 1e-30

    for (let colour = 0; colour < 2; colour++) {
      for (let j = 0; j < nz; j++) {
        for (let i = (j + colour) & 1; i < nr; i += 2) {
          const k = this.idx(i, j)

          let ap = this.etchApBase[k]

          // Wafer surface consumption, linearised about the current density.
          if (j === 0 && i < this.waferCells) {
            const density = Math.max(e[k], 1e-6)
            const consumed = p.surfaceSink(i, density)
            // consumed [m^-2 s^-1] / density [m^-3] gives a deposition velocity.
            ap += this.waferFaceArea[i] * (consumed / density)
          }
          if (ap <= 0) continue

          // Volume source: everything the plasma dissociated this pass.
          let sum =
            this.vol[k] * p.dissociationRate * this.plasmaShape[k] * this.feed[k] * yieldPerDiss

          if (i < nr - 1) sum += this.aE[k] * e[k + 1]
          if (i > 0) sum += this.aW[k] * e[k - 1]
          if (j < nz - 1) sum += this.aN[k] * e[k + nr]
          if (j > 0) sum += this.aS[k] * e[k - nr]

          const next = sum / ap
          const delta = Math.abs(next - e[k])
          if (delta > maxDelta) maxDelta = delta
          e[k] += OMEGA * (next - e[k])
          if (e[k] < 0) e[k] = 0
          if (e[k] > maxValue) maxValue = e[k]
        }
      }
    }

    return maxDelta / maxValue
  }

  /** Feed density along the wafer surface - what a deposition module reads. */
  feedSurfaceDensity(out: Float64Array): void {
    for (let i = 0; i < this.waferCells && i < out.length; i++) {
      out[i] = this.feed[this.idx(i, 0)]
    }
  }

  /** Radical density along the wafer surface, one value per radial cell. */
  surfaceDensity(out: Float64Array): void {
    for (let i = 0; i < this.waferCells && i < out.length; i++) {
      out[i] = this.etchant[this.idx(i, 0)]
    }
  }

  get waferCellCount(): number {
    return this.waferCells
  }

  /** Radius at the centre of wafer radial cell i [m]. */
  radiusAt(i: number): number {
    return this.grid.rc[i]
  }

  /** Sample the radical field for visualisation, normalised to its own peak. */
  normalisedEtchant(out: Float32Array): number {
    let peak = 1e-30
    for (let k = 0; k < this.etchant.length; k++) if (this.etchant[k] > peak) peak = this.etchant[k]
    for (let k = 0; k < this.etchant.length; k++) out[k] = clamp(this.etchant[k] / peak, 0, 1)
    return peak
  }

  /** Sample the feed field for visualisation, normalised to the inlet value. */
  normalisedFeed(out: Float32Array, inletDensity: number): void {
    const ref = Math.max(inletDensity, 1e-30)
    for (let k = 0; k < this.feed.length; k++) out[k] = clamp(this.feed[k] / ref, 0, 1)
  }
}
